import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Modal, Text } from '@mantine/core';

import type { BoardUserType } from 'types';
import { useDeleteOrganisationAdmin } from 'api/organisations/hooks/useDeleteOrganisationAdmin';
import { useShowNotification } from 'hooks';
import { usePostOrganisationAdmin } from 'api/organisations/hooks/usePostOrganisationAdmin';

type Props = {
  opened: boolean;
  onClose: () => void;
  user: BoardUserType;
};

type FormData = {
  permissions: BoardUserType['permissions'];
};

export const MemberAdminModal = ({
  opened,
  onClose,
  user: { id: userId, isAdminOrg },
}: Props) => {
  const { organisationId, boardId } = useParams() as {
    organisationId: string;
    boardId: string;
  };
  const { mutateAsync: deleteOrganisationAdmin } = useDeleteOrganisationAdmin();
  const { mutateAsync: postOrganisationAdmin } = usePostOrganisationAdmin();
  const showNotification = useShowNotification();
  const [actionPending, setActionPending] = useState(false);
  const { t } = useTranslation('organisations');

  const switchAdminPermissionsHandler = async () => {
    setActionPending(true);

    try {
      if (isAdminOrg) {
        await deleteOrganisationAdmin({
          organisationId,
          userId,
        });
      } else {
        await postOrganisationAdmin({
          pathParams: { organisationId },
          body: { userId },
        });
      }
      showNotification({
        variant: 'success',
        message: t('organisationView.boards.members.adminModal.successMessage'),
      });
      onClose();
    } catch (error) {
      showNotification({
        variant: 'error',
        message: t('organisationView.boards.members.adminModal.errorMessage'),
      });
    }

    setActionPending(false);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t(
        `organisationView.boards.members.adminModal.title.${
          isAdminOrg ? 'revoke' : 'grant'
        }`,
      )}
      size="md"
    >
      <Text mb="lg">
        {t(
          `organisationView.boards.members.adminModal.description.${
            isAdminOrg ? 'revoke' : 'grant'
          }`,
        )}
      </Text>
      <Flex justify="flex-end" gap={16}>
        <Button variant="default" onClick={onClose}>
          {t('organisationView.boards.members.adminModal.cancelButton')}
        </Button>
        <Button
          type="submit"
          loading={actionPending}
          onClick={switchAdminPermissionsHandler}
        >
          {t(
            `organisationView.boards.members.adminModal.button.${
              isAdminOrg ? 'revoke' : 'grant'
            }`,
          )}
        </Button>
      </Flex>
    </Modal>
  );
};
